/**
 * Mongo-style ObjectId: 8 hex digits of unix timestamp, 6 of machine id, 4 of process id
 * and 6 of a counter.
 */

const MACHINE_ID_KEY = "mongoMachineId"
const MACHINE_ID_MAX = 0xffffff

/** `bits` must be <= 32. */
function randomBits(bits: number): number {
  const word = new Uint32Array(1)
  crypto.getRandomValues(word)
  return word[0] >>> (32 - bits)
}

function parseMachineId(raw: string | null | undefined): number | undefined {
  if (!raw) return undefined
  const value = Number(raw)
  return Number.isInteger(value) && value >= 0 && value <= MACHINE_ID_MAX ? value : undefined
}

function cookieMachineId(): number | undefined {
  for (const entry of document.cookie.split("; ")) {
    const [name, value] = entry.split("=")
    if (name === MACHINE_ID_KEY) return parseMachineId(value)
  }
  return undefined
}

/**
 * Held steady per browser so two tabs of the same user don't mint colliding ids. Under Bun
 * there is no store and none is wanted: a per-process random value is all the field needs.
 * Storage can also throw outright (private browsing, blocked cookies), hence the catch.
 */
function resolveMachineId(): number {
  const fallback = randomBits(24)
  try {
    if (typeof localStorage === "object" && localStorage !== null) {
      const machine = parseMachineId(localStorage.getItem(MACHINE_ID_KEY)) ?? fallback
      localStorage.setItem(MACHINE_ID_KEY, String(machine))
      return machine
    }
    if (typeof document !== "undefined") {
      const machine = cookieMachineId() ?? fallback
      document.cookie = `${MACHINE_ID_KEY}=${machine};expires=Tue, 19 Jan 2038 05:00:00 GMT`
      return machine
    }
  } catch {
    // Unreachable storage is not worth failing over; the random id stands in for this session.
  }
  return fallback
}

const machineId = resolveMachineId()
const processId = randomBits(16)
let counter = randomBits(24)

function hex(value: number, digits: number): string {
  return value.toString(16).padStart(digits, "0")
}

export function objectId(): string {
  counter = (counter + 1) & 0xffffff
  return (
    hex(Math.floor(Date.now() / 1000), 8) + hex(machineId, 6) + hex(processId, 4) + hex(counter, 6)
  )
}
